import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import apiClient from "../api/apiClient";
import { useSnackbar } from "../context/SnackbarProvider";

const EditRegistrationDialog = ({ open, data, onClose, onSave }) => {
  const [registration, setRegistration] = useState({
    employeeId: "",
    fullName: "",
    email: "",
    phone: "",
    company: "",
  });
  const [eventType, setEventType] = useState("public");
  const [loading, setLoading] = useState({
    eventType: false,
  });
  const [errors, setErrors] = useState({});
  const showSnackbar = useSnackbar();

  useEffect(() => {
    const fetchEventType = async () => {
      setLoading((prev) => ({ ...prev, eventType: true }));

      try {
        // Assuming data contains the event ID
        const response = await apiClient.get(`/events/${data.eventId}`);
        setEventType(response.data.eventType || "public"); // Default to public if not specified
      } catch (err) {
        console.error("Error fetching event:", err);
        showSnackbar("Failed to fetch event details", "error");
        setEventType("public"); // Fallback to public event type
      } finally {
        setLoading((prev) => ({ ...prev, eventType: false }));
      }
    };
    if (open && data) {
      setRegistration({
        employeeId: data.employeeId || "",
        fullName: data.fullName || "",
        email: data.email || "",
        phone: data.phone || "",
        company: data.company || "",
      });

      fetchEventType();
    } else {
      setRegistration({
        employeeId: "",
        fullName: "",
        email: "",
        phone: "",
        company: "",
      });
      setEventType("public");
      setErrors({});
    }
  }, [open, data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRegistration((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};

    if (eventType === "public") {
      if (!registration.fullName?.trim()) {
        newErrors.fullName = "Full name is required";
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registration.email)) {
        newErrors.email = "Valid email is required";
      }
      if (!/^\+?[0-9]+$/.test(registration.phone)) {
        newErrors.phone = "Valid phone number required";
      }
    } else {
      if (!registration.employeeId?.trim()) {
        newErrors.employeeId = "Employee ID is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSave(registration);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: "primary.main",
          color: "white",
          textAlign: "center",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Edit Registration</Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {loading.eventType ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {eventType === "public" ? (
              <>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Full Name*"
                  name="fullName"
                  value={registration.fullName || ""}
                  onChange={handleChange}
                  error={!!errors.fullName}
                  helperText={errors.fullName}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Email*"
                  name="email"
                  value={registration.email || ""}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Phone*"
                  name="phone"
                  value={registration.phone || ""}
                  onChange={handleChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Company"
                  name="company"
                  value={registration.company || ""}
                  onChange={handleChange}
                />
              </>
            ) : (
              <TextField
                fullWidth
                margin="normal"
                label="Employee ID*"
                name="employeeId"
                value={registration.employeeId || ""}
                onChange={handleChange}
                error={!!errors.employeeId}
                helperText={errors.employeeId}
              />
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading.eventType}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading.eventType}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRegistrationDialog;
